/**
 * Table de libellés français.
 *
 * Une seule source de vérité pour l'UI (tâche 2), les exports et la matrice
 * de couverture : chaque code BT / code de nomenclature n'apparaît qu'ici.
 */
import type { InvoiceProfile } from './model';

export interface BtLabel {
  bt: string;
  label: string;
  section: SectionId;
}

export type SectionId = 'entete' | 'vendeur' | 'acheteur' | 'lignes' | 'tva' | 'totaux' | 'paiement' | 'livraison';

export const SECTIONS_FR: Record<SectionId, string> = {
  entete: 'En-tête',
  vendeur: 'Vendeur',
  acheteur: 'Acheteur',
  lignes: 'Lignes de facture',
  tva: 'Décomposition de la TVA',
  totaux: 'Totaux',
  paiement: 'Paiement',
  livraison: 'Livraison',
};

export const BT_LABELS_FR: Record<string, BtLabel> = {
  'BT-1': { bt: 'BT-1', label: 'Numéro de facture', section: 'entete' },
  'BT-2': { bt: 'BT-2', label: "Date d'émission", section: 'entete' },
  'BT-3': { bt: 'BT-3', label: 'Type de document', section: 'entete' },
  'BT-5': { bt: 'BT-5', label: 'Devise de facturation', section: 'entete' },
  'BT-6': { bt: 'BT-6', label: 'Devise de la TVA', section: 'entete' },
  'BT-9': { bt: 'BT-9', label: "Date d'échéance", section: 'paiement' },
  'BT-10': { bt: 'BT-10', label: "Référence acheteur", section: 'entete' },
  'BT-12': { bt: 'BT-12', label: 'Référence du contrat', section: 'entete' },
  'BT-13': { bt: 'BT-13', label: 'Numéro de commande', section: 'entete' },
  'BT-14': { bt: 'BT-14', label: 'Référence de commande vendeur', section: 'entete' },
  'BT-19': { bt: 'BT-19', label: 'Référence comptable acheteur', section: 'entete' },
  'BT-20': { bt: 'BT-20', label: 'Conditions de paiement', section: 'paiement' },
  'BT-22': { bt: 'BT-22', label: 'Mention libre', section: 'entete' },
  'BT-23': { bt: 'BT-23', label: 'Processus métier', section: 'entete' },
  'BT-24': { bt: 'BT-24', label: 'Spécification (profil)', section: 'entete' },
  'BT-27': { bt: 'BT-27', label: 'Nom du vendeur', section: 'vendeur' },
  'BT-28': { bt: 'BT-28', label: 'Enseigne commerciale du vendeur', section: 'vendeur' },
  'BT-29': { bt: 'BT-29', label: 'Identifiant du vendeur', section: 'vendeur' },
  'BT-30': { bt: 'BT-30', label: 'Identifiant légal du vendeur', section: 'vendeur' },
  'BT-31': { bt: 'BT-31', label: 'N° de TVA intracommunautaire du vendeur', section: 'vendeur' },
  'BT-32': { bt: 'BT-32', label: 'N° fiscal du vendeur', section: 'vendeur' },
  'BT-34': { bt: 'BT-34', label: 'Adresse électronique du vendeur', section: 'vendeur' },
  'BT-35': { bt: 'BT-35', label: 'Adresse du vendeur (ligne 1)', section: 'vendeur' },
  'BT-37': { bt: 'BT-37', label: 'Adresse du vendeur (ville)', section: 'vendeur' },
  'BT-38': { bt: 'BT-38', label: 'Code postal du vendeur', section: 'vendeur' },
  'BT-40': { bt: 'BT-40', label: 'Pays du vendeur', section: 'vendeur' },
  'BT-41': { bt: 'BT-41', label: 'Contact vendeur', section: 'vendeur' },
  'BT-42': { bt: 'BT-42', label: 'Téléphone du contact vendeur', section: 'vendeur' },
  'BT-43': { bt: 'BT-43', label: 'E-mail du contact vendeur', section: 'vendeur' },
  'BT-44': { bt: 'BT-44', label: "Nom de l'acheteur", section: 'acheteur' },
  'BT-45': { bt: 'BT-45', label: "Enseigne commerciale de l'acheteur", section: 'acheteur' },
  'BT-46': { bt: 'BT-46', label: "Identifiant de l'acheteur", section: 'acheteur' },
  'BT-47': { bt: 'BT-47', label: "Identifiant légal de l'acheteur", section: 'acheteur' },
  'BT-48': { bt: 'BT-48', label: "N° de TVA intracommunautaire de l'acheteur", section: 'acheteur' },
  'BT-49': { bt: 'BT-49', label: "Adresse électronique de l'acheteur", section: 'acheteur' },
  'BT-50': { bt: 'BT-50', label: "Adresse de l'acheteur (ligne 1)", section: 'acheteur' },
  'BT-52': { bt: 'BT-52', label: "Adresse de l'acheteur (ville)", section: 'acheteur' },
  'BT-53': { bt: 'BT-53', label: "Code postal de l'acheteur", section: 'acheteur' },
  'BT-55': { bt: 'BT-55', label: "Pays de l'acheteur", section: 'acheteur' },
  'BT-56': { bt: 'BT-56', label: "Contact acheteur", section: 'acheteur' },
  'BT-57': { bt: 'BT-57', label: 'Téléphone du contact acheteur', section: 'acheteur' },
  'BT-58': { bt: 'BT-58', label: 'E-mail du contact acheteur', section: 'acheteur' },
  'BT-59': { bt: 'BT-59', label: "Nom du destinataire du paiement", section: 'paiement' },
  'BT-60': { bt: 'BT-60', label: 'Identifiant du destinataire du paiement', section: 'paiement' },
  'BT-61': { bt: 'BT-61', label: 'Identifiant légal du destinataire du paiement', section: 'paiement' },
  'BT-62': { bt: 'BT-62', label: 'N° de TVA du destinataire du paiement', section: 'paiement' },
  'BT-72': { bt: 'BT-72', label: 'Date de livraison effective', section: 'livraison' },
  'BT-80': { bt: 'BT-80', label: 'Référence de la période de facturation', section: 'entete' },
  'BT-81': { bt: 'BT-81', label: 'Mode de paiement', section: 'paiement' },
  'BT-82': { bt: 'BT-82', label: 'Libellé du mode de paiement', section: 'paiement' },
  'BT-83': { bt: 'BT-83', label: 'Référence de paiement', section: 'paiement' },
  'BT-84': { bt: 'BT-84', label: 'IBAN', section: 'paiement' },
  'BT-86': { bt: 'BT-86', label: 'BIC', section: 'paiement' },
  'BT-89': { bt: 'BT-89', label: 'Référence du mandat de prélèvement', section: 'paiement' },
  'BT-106': { bt: 'BT-106', label: 'Total des lignes hors taxes', section: 'totaux' },
  'BT-107': { bt: 'BT-107', label: 'Total des remises globales', section: 'totaux' },
  'BT-108': { bt: 'BT-108', label: 'Total des majorations globales', section: 'totaux' },
  'BT-109': { bt: 'BT-109', label: 'Total hors taxes', section: 'totaux' },
  'BT-110': { bt: 'BT-110', label: 'Montant total de TVA', section: 'totaux' },
  'BT-111': { bt: 'BT-111', label: 'TVA totale dans la devise de TVA', section: 'totaux' },
  'BT-112': { bt: 'BT-112', label: 'Total toutes taxes comprises', section: 'totaux' },
  'BT-113': { bt: 'BT-113', label: 'Montant déjà payé', section: 'totaux' },
  'BT-114': { bt: 'BT-114', label: "Arrondi d'ajustement", section: 'totaux' },
  'BT-115': { bt: 'BT-115', label: 'Net à payer', section: 'totaux' },
  'BT-116': { bt: 'BT-116', label: 'Base hors taxes', section: 'tva' },
  'BT-117': { bt: 'BT-117', label: 'Montant de TVA', section: 'tva' },
  'BT-118': { bt: 'BT-118', label: 'Catégorie de TVA', section: 'tva' },
  'BT-119': { bt: 'BT-119', label: 'Taux de TVA', section: 'tva' },
  'BT-120': { bt: 'BT-120', label: "Motif d'exonération", section: 'tva' },
  'BT-121': { bt: 'BT-121', label: "Code d'exonération", section: 'tva' },
  'BT-126': { bt: 'BT-126', label: 'Numéro de ligne', section: 'lignes' },
  'BT-129': { bt: 'BT-129', label: 'Quantité', section: 'lignes' },
  'BT-130': { bt: 'BT-130', label: "Unité de mesure", section: 'lignes' },
  'BT-131': { bt: 'BT-131', label: 'Montant net de la ligne', section: 'lignes' },
  'BT-146': { bt: 'BT-146', label: 'Prix unitaire net', section: 'lignes' },
  'BT-147': { bt: 'BT-147', label: 'Remise sur la ligne', section: 'lignes' },
  'BT-148': { bt: 'BT-148', label: 'Prix unitaire brut', section: 'lignes' },
  'BT-149': { bt: 'BT-149', label: 'Quantité de base du prix', section: 'lignes' },
  'BT-151': { bt: 'BT-151', label: 'Catégorie de TVA de la ligne', section: 'lignes' },
  'BT-152': { bt: 'BT-152', label: 'Taux de TVA de la ligne', section: 'lignes' },
  'BT-153': { bt: 'BT-153', label: "Désignation de l'article", section: 'lignes' },
  'BT-154': { bt: 'BT-154', label: 'Description de la ligne', section: 'lignes' },
  'BT-155': { bt: 'BT-155', label: "Référence de l'article", section: 'lignes' },
};

export function btLabel(bt: string): string {
  return BT_LABELS_FR[bt]?.label ?? bt;
}

export const PROFILE_LABELS_FR: Record<InvoiceProfile, string> = {
  MINIMUM: 'Factur-X Minimum',
  BASIC_WL: 'Factur-X Basic WL',
  BASIC: 'Factur-X Basic',
  EN16931: 'Factur-X EN 16931',
  EXTENDED: 'Factur-X Extended',
  XRECHNUNG: 'XRechnung',
  UNKNOWN: 'Profil non reconnu',
};

/** Badge : texte court + ton visuel (cf. plan de badge du README). */
export const PROFILE_BADGE_FR: Record<InvoiceProfile, { short: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' }> = {
  MINIMUM: { short: 'MINIMUM', tone: 'warning' },
  BASIC_WL: { short: 'BASIC WL', tone: 'warning' },
  BASIC: { short: 'BASIC', tone: 'info' },
  EN16931: { short: 'EN 16931', tone: 'success' },
  EXTENDED: { short: 'EXTENDED', tone: 'info' },
  XRECHNUNG: { short: 'XRechnung', tone: 'success' },
  UNKNOWN: { short: 'Profil inconnu', tone: 'danger' },
};

export const DOCUMENT_TYPE_FR: Record<string, string> = {
  '380': 'Facture',
  '381': 'Avoir (note de crédit)',
  '384': 'Facture rectificative',
  '386': 'Facture d\'acompte',
  '389': 'Auto-facturation',
  '393': 'Facture de dédouanement',
  '395': 'Facture consulaire',
  '575': 'Facture de commission',
  '623': 'Facture de prestation',
  '780': 'Note d\'honoraires',
  '875': 'Facture partielle',
  '876': 'Facture d\'acompte',
  '877': 'Facture de solde',
};

export const TAX_CATEGORY_FR: Record<string, string> = {
  S: 'Taux normal',
  Z: 'Taux zéro',
  E: 'Exonéré',
  AE: 'Autoliquidation',
  K: 'Exportation hors UE',
  G: 'Exportation hors UE (bien)',
  O: 'Hors champ de la TVA',
  L: 'Livraison intracommunautaire',
  M: 'Livraison intracommunautaire exonérée',
  B: 'Transfert de droits d\'émission de GES',
};

export const PAYMENT_MEANS_FR: Record<string, string> = {
  '1': 'Instrument non défini',
  '2': 'Instrument non défini (2)',
  '10': 'Espèces',
  '20': 'Chèque',
  '30': 'Virement',
  '31': 'Virement (2)',
  '42': 'Virement sur compte bancaire',
  '48': 'Carte bancaire',
  '49': 'Prélèvement',
  '54': 'Carte de crédit',
  '55': 'Carte de débit',
  '57': 'Prélèvement (57)',
  '58': 'Prélèvement SEPA',
  '59': 'Prélèvement SEPA B2B',
  '68': 'Note de crédit',
  '97': 'Compensation',
  'ZZZ': 'Autre',
};

export const UNIT_FR: Record<string, string> = {
  C62: 'unité',
  H87: 'pièce',
  EA: 'unité',
  KGM: 'kilogramme',
  GRM: 'gramme',
  TNE: 'tonne',
  MTR: 'mètre',
  MTK: 'mètre carré',
  MTQ: 'mètre cube',
  LTR: 'litre',
  MLT: 'millilitre',
  KWH: 'kilowattheure',
  MWH: 'mégawattheure',
  DAY: 'jour',
  HUR: 'heure',
  MIN: 'minute',
  WEE: 'semaine',
  MON: 'mois',
  ANN: 'an',
  KM: 'kilomètre',
  KMT: 'kilomètre',
  LM: 'mètre linéaire',
  SET: 'lot',
  PK: 'paquet',
  BX: 'boîte',
  PR: 'paire',
  P1: 'pourcentage',
};

export const WARNING_FR: Record<string, string> = {
  NO_XML_ATTACHMENT: 'Aucune facture XML trouvée dans le PDF',
  NO_AF_ATTACHMENT: 'Aucune pièce jointe dans le PDF',
  MULTIPLE_XML: 'Plusieurs XML trouvés : le premier reconnu est utilisé',
  PROFILE_UNKNOWN: 'Profil (BT-24) absent ou non reconnu',
  PROFILE_MISMATCH: 'Le profil XMP du PDF diffère du BT-24',
  LEGACY_ZUGFERD1: 'Facture ZUGFeRD 1.0 : format obsolète, lecture partielle',
  NOT_AN_INVOICE: 'Le document XML n’est pas une facture',
  TOTALS_INCONSISTENT: 'Les totaux ne se recoupent pas',
  ENCRYPTED_PDF: 'PDF protégé : lecture impossible',
  PARSE_ERROR: 'Le XML est illisible',
  EMPTY_LINES: 'Aucune ligne de facture',
};

export function warningText(code: string): string {
  return WARNING_FR[code] ?? code;
}
